/**
 * Bilder und Videos zu einem Rezept: hochladen, sortieren und löschen.
 *
 * Das erste Bild ist das Titelbild. Die Reihenfolge wird am Server gespeichert.
 */
import { useRef, useState } from 'react';
import { ApiError, api } from '../api/client.ts';
import type { MediaItem } from '../api/types.ts';
import { AppIcon } from './AppIcon.tsx';
import { texts } from '../texts/de.ts';

/** Eigenschaften der Medienverwaltung. */
export interface RecipeMediaEditorProps {
  /** Kennung des Rezepts; ohne Kennung lässt sich nichts hochladen. */
  readonly recipeId: string | null;
  readonly media: readonly MediaItem[];
  readonly onChange: (media: MediaItem[]) => void;
}

/** Bilder und Videos verwalten. */
export function RecipeMediaEditor({ recipeId, media, onChange }: RecipeMediaEditorProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Lädt eine Datei hoch. */
  async function upload(file: File) {
    if (recipeId === null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(`/api/recipes/${recipeId}/media`, {
        method: 'POST',
        body: form,
      });
      const data = (await response.json()) as { media?: MediaItem; error?: string };
      if (!response.ok || data.media === undefined) {
        throw new ApiError(response.status, data.error ?? texts.common.error);
      }
      if (!media.some((item) => item.id === data.media?.id)) {
        onChange([...media, data.media]);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : texts.common.error);
    } finally {
      setBusy(false);
    }
  }

  /** Entfernt ein Medium. */
  async function remove(id: string) {
    if (recipeId === null) {
      return;
    }
    try {
      await api.delete(`/api/recipes/${recipeId}/media/${id}`);
      onChange(media.filter((item) => item.id !== id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  /** Setzt ein Bild an die erste Stelle. */
  async function makeCover(id: string) {
    if (recipeId === null) {
      return;
    }
    const chosen = media.find((item) => item.id === id);
    const next = [chosen, ...media.filter((item) => item.id !== id)].filter(
      (item): item is MediaItem => item !== undefined,
    );
    onChange(next);
    await api.put(`/api/recipes/${recipeId}/media-order`, { mediaIds: next.map((item) => item.id) });
  }

  return (
    <section className="rounded-card border border-border-subtle bg-surface-raised p-4">
      <h2 className="mb-2 font-semibold">{texts.editor.media}</h2>

      {recipeId === null ? (
        <p className="mb-3 rounded-xl bg-surface-sunken px-3 py-2 text-sm text-content-muted">
          {texts.editor.mediaSaveFirst}
        </p>
      ) : (
        <>
          <p className="mb-3 text-xs text-content-muted">{texts.editor.mediaHint}</p>

          <div className="mb-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => fileInput.current?.click()}
              className="inline-flex items-center gap-2 rounded-xl border border-border-subtle px-3 py-2 text-sm disabled:opacity-50"
            >
              <AppIcon name="plus" size={16} />
              {texts.editor.mediaAdd}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => cameraInput.current?.click()}
              className="inline-flex items-center gap-2 rounded-xl border border-border-subtle px-3 py-2 text-sm disabled:opacity-50"
            >
              <AppIcon name="bowl" size={16} />
              {texts.editor.mediaCamera}
            </button>
            {busy ? (
              <span className="self-center text-xs text-content-muted">{texts.editor.mediaUploading}</span>
            ) : null}
          </div>

          <input
            ref={fileInput}
            type="file"
            accept="image/*,video/*"
            multiple
            hidden
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              void (async () => {
                for (const file of files) {
                  await upload(file);
                }
              })();
            }}
          />
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file !== undefined) {
                void upload(file);
              }
            }}
          />
        </>
      )}

      {error === null ? null : (
        <p role="alert" className="mb-3 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">
          {error}
        </p>
      )}

      {media.length === 0 ? null : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {media.map((item, index) => (
            <li key={item.id} className="overflow-hidden rounded-xl border border-border-subtle">
              <div className="relative h-28 bg-surface-sunken">
                <img src={item.thumbUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                {index === 0 && item.kind === 'image' ? (
                  <span className="absolute top-1 left-1 rounded-full bg-accent px-2 py-0.5 text-[11px] text-on-accent">
                    {texts.editor.mediaCover}
                  </span>
                ) : null}
                {item.kind === 'video' ? (
                  <span className="absolute top-1 left-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white">
                    Video
                  </span>
                ) : null}
              </div>
              <div className="flex items-center justify-between gap-1 p-1.5">
                <button
                  type="button"
                  disabled={index === 0 || recipeId === null}
                  onClick={() => void makeCover(item.id)}
                  className="rounded-lg border border-border-subtle px-2 py-1 text-[11px] disabled:opacity-40"
                >
                  {texts.editor.mediaMakeCover}
                </button>
                <button
                  type="button"
                  onClick={() => void remove(item.id)}
                  aria-label={texts.editor.remove}
                  className="rounded-lg border border-border-subtle px-2 py-1 text-[11px] text-content-muted"
                >
                  ✕
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
